import { AppContainer } from 'action-handler';
import 'core-js';
import { Repository } from 'rest-adapter';
import { Watchlist } from 'watchlist';
import 'whatwg-fetch';
import { App, AppProps } from './App';
import { Config } from './Config';
import { Amplifier } from './Enum/Group/Amplifier';
import { Electronics } from './Enum/Group/Electronics';
import { ForceSensor } from './Enum/Group/ForceSensor';
import { LinearActuator } from './Enum/Group/LinearActuator';
import { MaterialSensor } from './Enum/Group/MaterialSensor';
import { MeasuringRoller } from './Enum/Group/MeasuringRoller';
import { SteeringFrame } from './Enum/Group/SteeringFrame';
import { TelemetrySystem } from './Enum/Group/TelemetrySystem';
import { TensionAccessories } from './Enum/Group/TensionAccessories';
import { TensionController } from './Enum/Group/TensionController';
import { WebGuideAccessories } from './Enum/Group/WebGuideAccessories';
import { WebGuideController } from './Enum/Group/WebGuideController';
import { FilterDeserializer } from './Filter/Serialization/FilterDeserializer';
import { FilterEquipService } from './Filter/Serialization/FilterEquipService';
import { FilterSerializer } from './Filter/Serialization/FilterSerializer';
import { UrlEquipServiceFactory } from './Filter/Serialization/UrlEquipServiceFactory';
import de from './i18n/de.json';
import en from './i18n/en.json';
import './index.css';
import { MainHandler } from './MainHandler';
import { Product } from './Model/Product';
import { FilterService } from './Service/FilterService';
import { LocalizationUtility } from './Service/LocalizationUtility';
import { RepositoryFactory } from './Service/RepositoryFactory';
import { RestAdapterFactory } from './Service/RestAdapterFactory';
import { TitleService } from './Service/TitleService';
import { TypeUtility } from './Service/TypeUtility';
import { ServiceLocator } from './ServiceLocator';
import * as serviceWorker from './serviceWorker';

// Customize this:
type ModelType = Product;

const serviceLocator = new ServiceLocator();
serviceLocator.registerMultiple(
    {
        config: () => {
            return Config.default;
        },
        restAdapterFactory: () => {
            return new RestAdapterFactory(serviceLocator, serviceLocator.get('config'));
        },
        adapter: () => {
            const factory: RestAdapterFactory = serviceLocator.get('restAdapterFactory');

            return factory.get();
        },

        repository: () => {
            const config: Config = serviceLocator.get('config');

            // Customize this:
            return new Repository(
                Product,
                config.language + '-products',
                serviceLocator.get('adapter')
            );
        },

        repositoryFactory: () => {
            return new RepositoryFactory(serviceLocator);
        },

        steeringFrameRepository: () => {
            return serviceLocator.get<RepositoryFactory>('repositoryFactory').build(SteeringFrame.ProductGroup);
        },
        linearActuatorRepository: () => {
            return serviceLocator.get<RepositoryFactory>('repositoryFactory').build(LinearActuator.ProductGroup);
        },
        webGuideControllerRepository: () => {
            return serviceLocator.get<RepositoryFactory>('repositoryFactory').build(WebGuideController.ProductGroup);
        },
        tensionControllerRepository: () => {
            return serviceLocator.get<RepositoryFactory>('repositoryFactory').build(TensionController.ProductGroup);
        },
        forceSensorRepository: () => {
            return serviceLocator.get<RepositoryFactory>('repositoryFactory').build(ForceSensor.Series);
        },
        amplifierRepository: () => {
            return serviceLocator.get<RepositoryFactory>('repositoryFactory').build(Amplifier.ProductGroup);
        },
        materialSensorRepository: () => {
            return serviceLocator.get<RepositoryFactory>('repositoryFactory').build(MaterialSensor.ProductGroup);
        },
        tensionAccessoriesRepository: () => {
            return serviceLocator.get<RepositoryFactory>('repositoryFactory').build(TensionAccessories.ProductGroup);
        },
        webGuideAccessoriesRepository: () => {
            return serviceLocator.get<RepositoryFactory>('repositoryFactory').build(WebGuideAccessories.ProductGroup);
        },
        telemetrySystemRepository: () => {
            return serviceLocator.get<RepositoryFactory>('repositoryFactory').build(TelemetrySystem.ProductGroup);
        },
        measuringRollerRepository: () => {
            return serviceLocator.get<RepositoryFactory>('repositoryFactory').build(MeasuringRoller.Series);
        },
        electronicsRepository: () => {
            return serviceLocator.get<RepositoryFactory>('repositoryFactory').build(Electronics.ProductGroup);
        },

        filterService: () => {
            return new FilterService();
        },
        filterEquipService: () => {
            return new FilterEquipService(new FilterDeserializer());
        },
        urlEquipServiceFactory: () => {
            return new UrlEquipServiceFactory(new FilterSerializer());
        },
        i18n: () => {
            return new LocalizationUtility(serviceLocator.get('config'), serviceLocator.get('translations'));
        },
        translations: () => {
            const config: Config = serviceLocator.get('config');
            const language = config.language;
            if (language === 'de') {
                return de;
            } else if (language === 'en') {
                return en;
            } else {
                return {};
            }
        },
        titleService: () => {
            return new TitleService(serviceLocator.get('i18n'));
        },
        typeUtility: () => {
            return new TypeUtility(serviceLocator);
        },
        handler: () => {
            return new MainHandler(serviceLocator);
        },
        watchlist: () => {
            return new Watchlist();
        }
    }
);

const handler: MainHandler<ModelType> = serviceLocator.get('handler');
const props: AppProps<ModelType> = {
    serviceLocator,
    handler,
};

const appContainer = new AppContainer(handler);
appContainer.render(App, document.getElementById('root') as HTMLElement, props);

// @ts-ignore
window['ProductFinder'] = {
    appContainer,
    serviceLocator,
};

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
